import { GetWithdrawAvailabilityResponse } from '../Earnings/Withdraw/Withdraw.types';
import { UserTicketsModel } from '../../types/loginTypes';

export enum WithdrawSteps {
  INITIAL = 1,
  FINAL_CHECK,
  SUCCESS,
  CHOOSE_BANK_ACCOUNT,
  ADD_BANK_ACCOUNT,
  TWO_FA_SETUP,
  VERIFICATION_EMAIL,
  TWO_FA_REQUIRED,
  AVAILABILITY
}

export enum WithdrawAvailability {
  ULX = 1,
  USDT
}

export interface UserPackage {
  id: number;
  nftId: number;
  packageId: number;
  type: string;
  name: string;
  price: number;
  image: string;
  baseStake: number;
  totalStake: number;
  autocompound: boolean;
  disableAtNextWeek: boolean;
  isAutostakeIntact: boolean;
  availableStake: number;
  dateLimitForAutoStake: string;
  dateNftWithdrawal: string;
  lastAutocompoundTimestamp: number;
  myEarnings: number;
  nextUnlockAmount: number;
  dateOfCreation: number;
  dateOfPurchase: number;
  nextUnlockDate: number;
  rsp: number;
  claimDate: string;
  potentialLoyaltyPoints: number;
  isConvertable: boolean;
  isWithdrawable: boolean;
  dateConverted: string | null;
  flipmeCardsRemaining?: number;
  flipmeCards?: any;
}

export interface PackageTrading {
  id: number;
  nftId?: number;
  name: string;
  title?: string;
  subtitle?: string;
  image: string;
  packageId: number;
  price: number;
  priceToShow: number | undefined | null;
  subscriptionVolume: number | undefined | null;
  dateOfPurchase: number; // Unix timestamp
  dateOfCreation: number; // Unix timestamp
  expiryDate: number; // Unix timestamp
  dateFinupBonus: string; // Unix timestamp
  maxDeposit: number | string;
  doubleTradingBinary: number;
  potentialLoyaltyPoints: number;
  isConvertable: boolean;
  isMemeBonusActivated: boolean;
  memeBonusActivationPrice: number;
  isActive?: boolean;
  depositedUsdtAmount?: number | null;
  depositedCurrencyAmount?: any;
  depositedTokenCurrency?: any;
  maxDepositAmount?: number | null;
  depositLimit?: number | null;
  nodeOnPercentage?: number;
  nodeOffPercentage?: number;
  mintedAmount?: number | null;
  mintedTokenCurrency?: string | null;
  flipmeCardsRemaining?: number;
  details: any;
  transferAssetStatus?: boolean;
}

export interface PackageBigWhale {
  id: number;
  packageId: number;
  name: string;
  image: string;
  price: number;
  dateOfCreation: string; // ISO date string
  dateOfPurchase: string; // ISO date string
  isConvertable: boolean;
  dateConverted: string; // ISO date string
  dateFinupBonus: string | undefined; // ISO date string
  loyaltyPoints: number;
  potentialLoyaltyPoints: number;
  isMemeBonusActivated: boolean;
}

export interface Summary {
  baseStake: number;
  totalStake: number;
  availableBalance: number;
  totalOrderAmount: number;
  totalOrderWithAutostakeIntactAmount: number;
  totalRsp: number;
  totalUsdAmount: number;
  availableUsdtToWithdraw: number;
  availableUsdtToClaim: number;
  flipmeSummary: number;
  totalNodeDepositOn?: number;
  totalNodeDepositOff?: number;
}

export interface CurrentVirtualOfficeLicence {
  licenceId: number;
  dateEnd: string;
  licenceName: string;
}

export interface MonthlyActivationStatus {
  licenceId: number;
  dateEnd: string;
  licenceName: string;
}

export interface PortfolioStakingPaymentGaming {
  summary: Summary;
  userPackages: UserPackage[];
  userAddress: string;
}

export interface SummaryTrading {
  totalOrderAmount: number;
  maxDeposit: number | string;
  tradingBinary: number;
}

export interface SummaryBigWhale {
  totalOrderAmount: number;
  totalBigWhalePoints: number;

  subscriptionVolume: number;

  memeCoinsFromBigWhale: number;
  memeCoinsFromBring3: number;
  changePeriodInSeconds: number;
  finupCoinsPerChange: number;
  fastStartCoinsPerChange: number;

  memeCoins: number;
  totalMemeCoins: number;
  totalCoinsPerChange: number;

  silverPoolTickets: number;
  goldPoolTickets: number;
  platinumPoolTickets: number;
  ultraVipPoolTickets: number;
}

export interface PortfolioTrading {
  userPackages: PackageTrading[];
  summary: SummaryTrading;
}

export interface PortfolioBigWhale {
  userPackages: PackageBigWhale[];
  summary: SummaryBigWhale;
}

export interface PortfolioMonthlyActivity {
  hasActiveVirtualOfficeSubscription: boolean;
  currentVirtualOfficeLicence: CurrentVirtualOfficeLicence;
  hasActivePartnerStatus: boolean;
  hasUserPackage: boolean;
  hasActiveMonthlyActivation: boolean;
  monthlyActivationStatus: MonthlyActivationStatus;
}

export interface PortfolioWalletSummary {
  totalOrder: number;
  totalOrderAutostakeIntact: number;
  availableBalance: number;
  availableUsdtToWithdraw: number;
  userAddress: string;
}

export interface ChangeAutoStakeForPackageResponse {
  userPackage: {
    id: number;
    isAutostakeIntact: boolean;
  };
}
export interface GetStakingHubUlxWithdrawalInfoResponse {
  requestedUlxAmount: number;
  ulxWitdrawalAmount: number;
  ulxAmountToBurn: number;
}

export interface GetMyHubsSummaryResponse {
  currentVirtualOfficeLicence: CurrentVirtualOfficeLicence;
  qualifiedRank: string;
  lifetimeRank: string;
  walletSummary: PortfolioWalletSummary;
  withdrawAvailability: GetWithdrawAvailabilityResponse;
  userAddress: string;
  exchangeRates?: any;
}

export interface GetMyHubsTradingResponse {
  userPackages: PackageTrading[];
  summary: SummaryTrading;
}

export interface GetMyHubsBigWhaleResponse {
  userPackages: PackageBigWhale[];
  summary: SummaryBigWhale;
}

export interface GetMyHubsPaymentResponse {
  summary: Summary;
  flipMeSummary: number;
  userPackages: UserPackage[];
  userAddress: string;
}

export interface GetMyHubsGamingResponse {
  summary: Summary;
  userPackages: UserPackage[];
  userAddress: string;
}

export interface GetMyHubsStakingResponse {
  summary: Summary;
  userPackages: UserPackage[];
  userAddress: string;
}

export interface GetConversionInfoResponse {
  amount: number;
  limitDate: string;
  infoMessages: any;
}

export interface UserPackageModel {
  id: number;
  price: number;
  shoppingPoints: number;
  donationPoints: number;
  teamBonusPercent: number;
  status: number;
  dateCreated: string;
  datePaid: string;
  networkType: number;
  paymentAddress: string;
  package: {
    packageId: number;
    name: string;
    price: number;
    teamBonusPercent: number;
    order: number;
    stakeRatio: number;
    weekCapRuleLimit: number;
    volumePoints: number;
    type: number;
  };
  packageRank: {
    packageRankId: number;
    name: string;
  };
  nextPackageRank: {
    packageRankId: number;
    name: string;
  };
}

export interface BigWhaleUserPackage {
  id: number;
  price: number;
  shoppingPoints: number;
  donationPoints: number;
  teamBonusPercent: number;
  status: number;
  dateCreated: string;
  datePaid: string;
  networkType: number;
  paymentAddress: string;
  package: {
    packageId: number;
    name: string;
    price: number;
    teamBonusPercent: number;
    order: number;
    stakeRatio: number;
    weekCapRuleLimit: number;
    volumePoints: number;
    type: number;
  };
  dateFinupBonus: string;
  memeBonusActivationId: number;
}

export interface AddBigWhaleVoucherResponse {
  userPackage: UserPackageModel;
  userTickets: UserTicketsModel;
  totalAmount: number;
  bigWhaleUserPackage: BigWhaleUserPackage;
}

export interface WillyCoinsSummaryResponse {
  totalMemeCoins: number;
  memeCoinsFromBigWhale: number;
  memeCoinsFromBring3: number;
  changePeriodInSeconds: number;
  fastStartCoinsPerChange: number;
  finupCoinsPerChange: number;
  totalCoinsPerChange: number;
}
